import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { useAuth } from 'hooks';
import { Links } from 'settings';
import { EquipmentTabsValues } from 'types';

import { createEquipmentsTabs } from '../../../components/constants';

interface LocationI {
  pathname: string;
  state?: { from: string };
}

const TAB_PARAM_KEY = 'tab';

export function useEquipmentTabs() {
  const [activeTab, setActiveTab] = useState<EquipmentTabsValues>();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { employeeId } = useParams();
  const { pathname, state } = useLocation() as LocationI;
  const { user } = useAuth();

  const tabParam = searchParams.get(TAB_PARAM_KEY);
  const currentTab = useMemo(
    () => createEquipmentsTabs.find(({ value }) => value === tabParam),
    [tabParam],
  );

  useEffect(() => {
    if (!currentTab) {
      setActiveTab(EquipmentTabsValues.REGISTER);
    } else if (tabParam && currentTab) {
      setSearchParams(searchParams, { replace: true });
      setActiveTab(currentTab.value);
    } else if (tabParam && !currentTab) {
      searchParams.delete(TAB_PARAM_KEY);
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActionsPage = pathname.includes(Links.actions.createEquipment);
  const actionsPath = generatePath(Links.actions.createEquipment);
  const profilePath = generatePath(Links.profile.equipmentList.createEquipment, {
    employeeId: employeeId || user?.id || '',
  });

  const handleChange = useCallback(
    (e: SyntheticEvent, newValue: EquipmentTabsValues) => {
      e.preventDefault();

      const newState = { from: state?.from ?? '' };
      const equipmentTabLink = {
        pathname: isActionsPage ? actionsPath : profilePath,
        search: `?tab=${newValue}`,
      };

      navigate(equipmentTabLink, {
        state: newState,
        replace: true,
      });
      setActiveTab(newValue);
    },
    [actionsPath, isActionsPage, navigate, profilePath, state?.from],
  );

  return {
    activeTab: activeTab ?? EquipmentTabsValues.REGISTER,
    handleChange,
  };
}
